import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { cloneDeep } from 'lodash-es';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ITask } from '@nimbus-assistant/models/task.model';
import { TaskFlowDetails, CallDirection, TaskFlowParticipantType, Modality } from '@nimbus-assistant/models/task-types.model';
import { Utilities } from '@nimbus-assistant/utils/utilities';
import { AssistantTasksService } from '@nimbus-assistant/services/assistant-tasks.service';
import { ConversationTaskType } from '@nimbus-global/features/reportingLiveData/models/task-type.enum';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { IconWeight } from '@nimbus-library/features/shared/directives/icon-weight.enum';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import {
  ConsultationSessionState,
  ConsultationSessionTerminationReason,
} from '@nimbus-library/features/shared/models/participant-info/i-combined-conversation.response';

@Component({
  standalone: true,
  selector: 'app-task-flow-tab',
  templateUrl: './task-flow-tab.component.html',
  styleUrls: ['./task-flow-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, SharedUIModule],
})
export class TaskFlowTabComponent implements OnChanges, OnInit {
  private static readonly LAST_ELEMENT_INDEX = -1;
  private static readonly MIN_FLOW_LENGTH_WITH_INTERMEDIARIES = 2;

  @Input() public task: ITask;

  public readonly TranslationKeys = TranslationKeys;
  public readonly icons = ICONS;
  public readonly IconWeight = IconWeight;

  public transferors$ = new BehaviorSubject<TaskFlowDetails[]>(null);
  public initialCaller$ = new BehaviorSubject<TaskFlowDetails>(null);
  public connectedService$ = new BehaviorSubject<TaskFlowDetails>(null);
  public isOutboundCall$ = new BehaviorSubject<boolean>(false);
  public loading$: Observable<boolean> = of(false);

  public constructor(
    private readonly translateService: TranslateService,
    private readonly tasksService: AssistantTasksService,
  ) {}

  public ngOnInit(): void {
    this.loading$ = this.tasksService.callFlowLoading$.pipe(
      map((ids) => ids.has(this.task?.unifiedConversationId)),
      distinctUntilChanged(),
    );
  }

  public ngOnChanges(): void {
    if (this.task) {
      this.updateCallFlow(this.task);
    }
  }

  public getTransferorAction(transferor: TaskFlowDetails): string {
    switch (transferor.participantType) {
      // For an outbound call the flow is reversed to read service -> target, so the target
      // (participantType Caller) ends up in the position that carries the connecting action.
      case TaskFlowParticipantType.IncomingCall:
      case TaskFlowParticipantType.Caller:
        return this.translateService.instant(this.getSessionAction());
      case TaskFlowParticipantType.Transfer:
        return this.translateService.instant(TranslationKeys.CallFlowTabTransfer);
      case TaskFlowParticipantType.ConsultationToUser:
      case TaskFlowParticipantType.ConsultationToService:
        return this.translateService.instant(TranslationKeys.CallFlowTabConsultationCall);
      default:
        return '';
    }
  }

  private getSessionAction(): string {
    switch (this.task?.modality) {
      case Modality.IM:
        return TranslationKeys.CallFlowTabIm;
      case Modality.Email:
        return TranslationKeys.CallFlowTabEmail;
      case Modality.ExternalTask:
        return TranslationKeys.CallFlowTabAccepted;
      case Modality.Audio:
        if (this.task?.direction !== CallDirection.Outbound) {
          return TranslationKeys.CallFlowTabCall;
        }
        // Workflow already connected the target before the agent joined, so the agent is
        // "connected to" an outbound call rather than actively "calling" it, unlike call-on-behalf.
        switch (this.task?.taskType) {
          case ConversationTaskType.OutboundCallWithWorkflow:
            return TranslationKeys.CallFlowTabOutboundCall;
          case ConversationTaskType.OutboundCallOnBehalf:
            return TranslationKeys.CallFlowTabCallOnBehalf;
          default:
            return TranslationKeys.CallFlowTabCalling;
        }
      default:
        return TranslationKeys.CallFlowTabCall;
    }
  }

  private updateCallFlow(task: ITask): void {
    const isOutboundCall = task.direction === CallDirection.Outbound;
    this.isOutboundCall$.next(isOutboundCall);

    // callFlow is already ordered service -> target for outbound, caller -> service for inbound,
    // by whoever produced it (BE or the local fallback builder) - trust it as-is.
    const filteredCallFlow = this.filterCallFlowDetails(task);

    if (!filteredCallFlow?.length) {
      this.initialCaller$.next(null);
      this.connectedService$.next(null);
      this.transferors$.next(null);
      return;
    }

    const initialCaller = filteredCallFlow[0];
    if (Utilities.isValidUuid(initialCaller.serviceName)) {
      initialCaller.serviceName = this.translateService.instant(TranslationKeys.GeneralAnonymous);
    }
    this.initialCaller$.next(initialCaller);

    if (filteredCallFlow.length > 1) {
      const connectedService = filteredCallFlow.slice(TaskFlowTabComponent.LAST_ELEMENT_INDEX)[0];
      if (Utilities.isValidUuid(connectedService.serviceName)) {
        connectedService.serviceName = this.translateService.instant(TranslationKeys.GeneralAnonymous);
      }
      this.connectedService$.next(connectedService);
    } else {
      this.connectedService$.next(null);
    }

    if (filteredCallFlow.length > TaskFlowTabComponent.MIN_FLOW_LENGTH_WITH_INTERMEDIARIES) {
      const transferors = filteredCallFlow.slice(1, filteredCallFlow.length - 1);
      this.transferors$.next(transferors);
    } else {
      this.transferors$.next(null);
    }
  }

  private filterCallFlowDetails(task: ITask): TaskFlowDetails[] {
    if (!task?.callFlow?.length) {
      return [];
    }

    const consultation = task.consultationSession;
    const consultationCallId = consultation?.id;
    const consultationState = consultation?.currentState;
    const terminationReason = consultation?.terminationReason;

    return cloneDeep(task.callFlow).filter((item) => {
      const isConsultationCall =
        item.participantType === TaskFlowParticipantType.ConsultationToUser ||
        item.participantType === TaskFlowParticipantType.ConsultationToService;

      if (!isConsultationCall) {
        return true;
      }

      if (!consultation || !consultationCallId) {
        return false;
      }

      const isCurrentConsultation = item.consultationSessionId === consultationCallId;
      if (!isCurrentConsultation) {
        return false;
      }

      if (consultationState === ConsultationSessionState.Completed) {
        return terminationReason === ConsultationSessionTerminationReason.Transferred;
      }

      if (consultationState === ConsultationSessionState.Conferenced && consultation.isTargetTerminated) {
        return false;
      }

      return consultationState !== ConsultationSessionState.Canceled;
    });
  }
}
